import { Injectable } from '@angular/core'
import {
  CompanyAPIModel,
  CompanyEntityAPIModel,
  CompanyGroupAdditionalDetailsOrchApiModel,
  EmployeeAPIModel,
  ScreenConfigApiModel,
  SopSettingsAPIModel,
  UserAPIModel
} from '../../models/api.model'

import { map, Observable, of } from 'rxjs'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { WebService } from '../api/web.service'
import { save_company_action, save_entities, save_sop_settings } from '../../store/action/onboarding.action'
import { AppState } from '../../store/state/app.state'
import { Store } from '@ngrx/store'

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  constructor(private webservice: WebService, private appState: Store<AppState>) {}

  dispatchSaveCompanyAction(company: CompanyAPIModel, routeTo: string) {
    this.appState.dispatch(save_company_action({ company, routeTo }))
  }

  saveCompanyApi(company: CompanyAPIModel) {
    const endPoint = ServiceUrlConstants.API_ONBOARDING_SAVE_COMPANY
    return this.webservice.post(endPoint, company, null, [], false).pipe(map((data: CompanyAPIModel) => data))
  }

  saveEntitiesApi(entities: CompanyEntityAPIModel[]) {
    const endPoint = ServiceUrlConstants.API_ONBOARDING_SAVE_COMPANY_ENTITIES
    return this.webservice.post(endPoint, entities, null, [], false)
  }

  dispatchSaveEntitiesAction(entities: CompanyEntityAPIModel[]) {
    this.appState.dispatch(save_entities({ entities }))
  }

  getUnassignedRmsAdmins(companyGroupId: string): Observable<UserAPIModel[]> {
    const endPoint = ServiceUrlConstants.API_GET_UNASSIGNED_RMS_ADMINS
    const payload = {
      companyGroupId,
      roleCodes: ['RMS_SYS_ADMIN', 'EMP_ADMIN']
    }
    return this.webservice.post(endPoint, payload, null, [], false)
  }

  dispatchSaveSopSettingsAction(payload: SopSettingsAPIModel) {
    this.appState.dispatch(save_sop_settings({ payload }))
  }

  saveSopSettingsApi(payload: any) {
    const endPoint = ServiceUrlConstants.API_ONBOARDING_SAVE_SOP_SETTINGS
    return this.webservice.post(endPoint, payload, null, [], false)
  }

  // -------- STEP 4: EMPLOYEES --------
  searchEmployees(searchText: string, entityIds: string[]) {
    const endPoint = ServiceUrlConstants.API_ONBOARDING_EMP_SEARCH
    return this.webservice.post(endPoint, { searchText, entityIds }).pipe(map((res: any) => res?.content || []))
  }

  assignEmployeeAdmins(payload: {
    employeeIds: string[]
    companyReferenceType: 'GROUP' | 'ENTITY'
    companyReferenceId: string
  }) {
    const endpoint = ServiceUrlConstants.API_ONBOARDING_ASSIGN_EMPLOYEE_ADMINS
    return this.webservice.post(endpoint, payload, null, [], false)
  }

  // -------- STEP 5: SCREEN CONFIG --------
  save(config: Partial<ScreenConfigApiModel>) {
    return this.webservice.put(`${ServiceUrlConstants.API_SAVE_SCREEN_CONFIG}/${config.id}`, config)
  }

  getConfigsByCompanyGroup(companyGroupId: string): Observable<ScreenConfigApiModel[]> {
    return this.webservice.get(`${ServiceUrlConstants.API_GET_SCREEN_CONFIGS_BY_CONFIG_GROUP_ID}/${companyGroupId}`)
  }

  // -------- STEP 6: OTHERS --------
  saveOthers(
    companyGroupId: string,
    others: CompanyGroupAdditionalDetailsOrchApiModel,
    completeOnboarding: boolean
  ): Observable<CompanyAPIModel> {
    const endPoint = `/company-group/${companyGroupId}/additional-details?completeOnboarding=${completeOnboarding}`
    return this.webservice.put(endPoint, others, null, [], false).pipe(map((data: CompanyAPIModel) => data))
  }

  // -------- EDIT MODE: LOAD DATA --------
  loadOnboarding(companyId: string): Observable<{
    company: CompanyAPIModel
    entities: CompanyEntityAPIModel[]
    sopSettings: SopSettingsAPIModel
    employees: EmployeeAPIModel[]
    others: CompanyGroupAdditionalDetailsOrchApiModel
  }> {
    // TEMP MOCK
    return of({
      company: undefined,
      entities: undefined,
      sopSettings: undefined,
      employees: undefined,
      others: undefined
    })
  }

  //Mapper for backend

  mapCompanyFormToPayload(form: any): CompanyAPIModel {
    return {
      id: null,
      createdBy: null,
      updatedBy: null,
      status: null,
      displayId: form.companyDisplayId,
      code: '',
      name: form.companyName,
      address: {
        id: null,
        createdBy: null,
        updatedBy: null,
        status: null,
        address1: form.doorNo,
        address2: form.street,
        address3: form.area,
        city: form.city,
        state: form.state,
        country: form.country,
        pin: form.pincode
      },
      additionalDetails: {
        id: null,
        triggerEmployeeWelcomeEmail: false,
        triggerAdminWelcomeEmail: false,
        tenantId: null,
        clientId: null,
        companyDomain: null,
        authType: null,
        ssoProvider: null,
        employeeAndEeAdminNotificationsEnabled: false
      }
    }
  }

  mapEntitiesFormToPayload(formValue: any, companyGroupId: string) {
    return formValue.entities.map((e: any) => ({
      id: null,
      displayId: e.entityDisplayId,
      status: null,

      companyGroup: { id: companyGroupId },

      code: null,
      name: e.entityName,
      address: null,

      contactPersonName: e.contactPersonName,
      contactPersonEmail: e.contactPersonEmail,
      contactPersonMobile: e.contactPersonMobile,

      locations: e.locations.map((l: any) => ({
        id: null,
        companyEntityId: null,
        companyGroupId: companyGroupId,
        code: l.locationCode,
        address: {
          id: null,
          address1: l.address.doorNo,
          address2: l.address.street,
          address3: l.address.area,
          city: l.address.city,
          state: l.address.state,
          country: l.address.country,
          pin: l.address.pincode
        }
      }))
    }))
  }

  mapSopFormToPayload(form: any, companyGroupId: string): SopSettingsAPIModel {
    const notificationPrefs = {
      EMAIL: true, // ensure EMAIL is added since it's disabled in UI
      ...(form.notificationPreferences || {})
    }
    return {
      companyGroupId,
      modules: Object.keys(form.modules).filter(key => form.modules[key]),
      categories: Object.keys(form.categories || {}).filter(key => form.categories[key]),
      notificationPreferences: Object.keys(notificationPrefs).filter(key => notificationPrefs[key]),
      rmsAdminUserIds: (form.rmsAdminUserIds || []).map((u: any) => u.value)
    }
  }
}
